// First Created - 25 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board
//           candidate Pre Program Actions / onboarding journey.
//
// Supports:
//
// - board level onboarding status
// - board level SlotBoard booking status
// - Initial 1:1 booking status
// - Deep Dive booking status
// - one candidate onboarding record
// - update / create onboarding state
// - reset one Pre Program Action
// - archive onboarding record
//
// Access is enforced by the backend:
//
// - EtAdmin        → any organization
// - EtCoach        → any organization
// - OrgSponsor     → own organization only
// - OrgFacilitator → own organization only
//
// Updated - 25 Sep 2026
// Added SlotBoard booking status query for:
//
// - Initial 1:1
// - Deep Dive
//
// Updated - 27 Sep 2026 - added my initiation endpoint

import {
  createSelector,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'



/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionCandidateOnboardingsApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        
        /*
        |--------------------------------------------------------------------------
        | Get My Initiation - 27 Sep 2026
        |--------------------------------------------------------------------------
        |
        | GET /orgIntentionCandidateOnboarding/my-initiation
        |
        | Personal authenticated endpoint.
        |
        | No frontend supplied parameters.
        |
        | Returns:
        |
        | {
        |   exists: boolean,
        |   journeys: [
        |     {
        |       intentionBoard,
        |       candidate,
        |       onboarding,
        |       bookings
        |     }
        |   ]
        | }
        |
        */

        getMyOrgIntentionCandidateInitiation:
            builder.query({

                query:
                () => ({

                    url:
                    '/orgIntentionCandidateOnboarding/my-initiation',

                    validateStatus:
                    (
                        response,
                        result
                    ) => {

                        return (
                        response.status === 200 &&
                        !result.isError
                        )

                    },

                }),


                transformResponse:
                responseData => {

                    const journeys =
                    responseData?.journeys || []

                    return {

                    exists:
                        Boolean(
                        responseData?.exists &&
                        journeys.length > 0
                        ),

                    journeys,

                    }

                },


                /*
                |--------------------------------------------------------------------------
                | Personal Journey Cache
                |--------------------------------------------------------------------------
                |
                | A personal journey may be affected by administrative changes to
                | any associated candidate onboarding record.
                |
                */

                providesTags:
                result => [

                    {
                    type:
                        'OrgIntentionCandidateOnboarding',

                    id:
                        'MY-INITIATION',
                    },

                    ...(result?.journeys || [])
                    .filter(
                        journey =>
                        journey?.candidate?._id
                    )
                    .map(
                        journey => ({

                        type:
                            'OrgIntentionCandidateOnboarding',

                        id:
                            `CANDIDATE-${journey.candidate._id}`,

                        })
                    ),

                ],

                refetchOnMountOrArgChange:
                true,

            }),


        /*
        |--------------------------------------------------------------------------
        | Get Board Candidate Onboarding Status
        |--------------------------------------------------------------------------
        |
        | GET
        |
        | /orgIntentionCandidateOnboarding/status
        |
        | Query:
        |
        | orgId
        | intentionBoardId
        |
        | Returns:
        |
        | {
        |   count,
        |   candidates: [
        |     {
        |       intentionCandidateId,
        |       userId,
        |       summaryStatus,
        |       actions: {
        |         contactDetails,
        |         digitalPic,
        |         avConsent
        |       },
        |       updatedAt
        |     }
        |   ]
        | }
        |
        */

        getOrgIntentionBoardCandidateOnboardingStatus:
          builder.query({

            query:
              ({
                orgId,
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionCandidateOnboarding/status',

                params: {

                  orgId,

                  intentionBoardId,

                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Transform Response
            |--------------------------------------------------------------------------
            |
            | Keep board status intentionally lightweight.
            |
            | No entity adapter is needed here because this is a status
            | projection used directly by the Initiation journey board.
            |
            */

            transformResponse:
              responseData => {

                const candidates =
                  responseData
                    ?.candidates
                  ||
                  []


                return {

                  count:
                    responseData
                      ?.count
                    ??
                    candidates
                      .length,

                  candidates,

                }

              },


            /*
            |--------------------------------------------------------------------------
            | Tags
            |--------------------------------------------------------------------------
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidates =
                  result
                    ?.candidates
                  ||
                  []


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `BOARD-${arg?.intentionBoardId}`,
                  },


                  ...candidates
                    .filter(
                      candidate =>
                        candidate
                          ?.intentionCandidateId
                    )
                    .map(
                      candidate => ({

                        type:
                          'OrgIntentionCandidateOnboarding',

                        id:
                          `CANDIDATE-${candidate.intentionCandidateId}`,

                      })
                    ),

                ]

              },

          }),



        /*
        |--------------------------------------------------------------------------
        | Get Board Candidate SlotBoard Status - 25 Sep 2026
        |--------------------------------------------------------------------------
        |
        | GET
        |
        | /orgIntentionCandidateOnboarding/slot-status
        |
        | Query:
        |
        | orgId
        | intentionBoardId
        |
        | Returns:
        |
        | {
        |   count,
        |   candidates: [
        |     {
        |       intentionCandidateId,
        |
        |       initial1on1: {
        |         status,
        |         schedulingPageId,
        |         slotId,
        |         slotStart,
        |         slotEnd,
        |         slotTimezone,
        |         bookedByTimezone,
        |         bookedAt
        |       },
        |
        |       deepDive: {
        |         status,
        |         schedulingPageId,
        |         slotId,
        |         slotStart,
        |         slotEnd,
        |         slotTimezone,
        |         bookedByTimezone,
        |         bookedAt
        |       }
        |     }
        |   ]
        | }
        |
        | Status:
        |
        | booked
        | not_booked
        |
        | SlotBoard source:
        |
        | initial_1on1 communication
        |     -> metadata.slotBoardId
        |
        | deep_dive communication
        |     -> metadata.slotBoardId
        |
        */

        getOrgIntentionBoardCandidateSlotStatus:
          builder.query({

            query:
              ({
                orgId,
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionCandidateOnboarding/slot-status',

                params: {

                  orgId,

                  intentionBoardId,

                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Transform Response
            |--------------------------------------------------------------------------
            */

            transformResponse:
              responseData => {

                const candidates =
                  responseData
                    ?.candidates
                  ||
                  []


                return {

                  count:
                    responseData
                      ?.count
                    ??
                    candidates
                      .length,

                  candidates,

                }

              },


            /*
            |--------------------------------------------------------------------------
            | Tags
            |--------------------------------------------------------------------------
            |
            | Keep SlotBoard journey status separate from the persisted
            | Pre Program Action board status.
            |
            | We still use the existing onboarding tag type so no additional
            | apiSlice tag type is required.
            |
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidates =
                  result
                    ?.candidates
                  ||
                  []


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `SLOT-BOARD-${arg?.intentionBoardId}`,
                  },


                  ...candidates
                    .filter(
                      candidate =>
                        candidate
                          ?.intentionCandidateId
                    )
                    .map(
                      candidate => ({

                        type:
                          'OrgIntentionCandidateOnboarding',

                        id:
                          `SLOT-CANDIDATE-${candidate.intentionCandidateId}`,

                      })
                    ),

                ]

              },

          }),



        /*
        |--------------------------------------------------------------------------
        | Get One Candidate Onboarding
        |--------------------------------------------------------------------------
        |
        | GET
        |
        | /orgIntentionCandidateOnboarding
        |
        | Query:
        |
        | orgId
        | intentionBoardId
        | intentionCandidateId
        |
        | Backend may return either:
        |
        | exists: true
        |     persisted onboarding document
        |
        | exists: false
        |     derived pending onboarding state
        |
        */

        getOrgIntentionCandidateOnboarding:
          builder.query({

            query:
              ({
                orgId,
                intentionBoardId,
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidateOnboarding',

                params: {

                  orgId,

                  intentionBoardId,

                  intentionCandidateId,

                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Tags
            |--------------------------------------------------------------------------
            */

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                const onboardingId =
                  result
                    ?.onboarding
                    ?._id


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `CANDIDATE-${arg?.intentionCandidateId}`,
                  },


                  ...(
                    onboardingId
                      ?
                      [
                        {
                          type:
                            'OrgIntentionCandidateOnboarding',

                          id:
                            onboardingId,
                        },
                      ]
                      :
                      []
                  ),

                ]

              },

          }),



        /*
        |--------------------------------------------------------------------------
        | Update Candidate Onboarding
        |--------------------------------------------------------------------------
        |
        | PATCH
        |
        | /orgIntentionCandidateOnboarding/update
        |
        | Body:
        |
        | {
        |   orgId,
        |   intentionBoardId,
        |   intentionCandidateId,
        |   actionName?,
        |   status?,
        |   onboardingNotes?,
        |   userId?
        | }
        |
        | actionName:
        |
        | contactDetails
        | digitalPic
        | avConsent
        |
        | status:
        |
        | pending
        | completed
        | not_required
        |
        | The backend creates the onboarding record when the first
        | meaningful update occurs.
        |
        */

        updateOrgIntentionCandidateOnboarding:
          builder.mutation({

            query:
              onboardingUpdate => ({

                url:
                  '/orgIntentionCandidateOnboarding/update',

                method:
                  'PATCH',

                body: {
                  ...onboardingUpdate,
                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Invalidate Candidate + Board Status
            |--------------------------------------------------------------------------
            |
            | Any candidate onboarding update can alter:
            |
            | - candidate onboarding details
            | - board level summaryStatus
            | - individual Pre Program Action status
            |
            */

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const onboardingId =
                  result
                    ?.onboarding
                    ?._id


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `BOARD-${arg?.intentionBoardId}`,
                  },


                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `CANDIDATE-${arg?.intentionCandidateId}`,
                  },


                  ...(
                    onboardingId
                      ?
                      [
                        {
                          type:
                            'OrgIntentionCandidateOnboarding',

                          id:
                            onboardingId,
                        },
                      ]
                      :
                      []
                  ),

                ]

              },

          }),



        /*
        |--------------------------------------------------------------------------
        | Reset Candidate Onboarding Action
        |--------------------------------------------------------------------------
        |
        | PATCH
        |
        | /orgIntentionCandidateOnboarding/reset
        |
        | Body:
        |
        | {
        |   orgId,
        |   intentionBoardId,
        |   intentionCandidateId,
        |   actionName
        | }
        |
        | Backend resets:
        |
        | status            → pending
        | completedAt       → null
        | completedByUserId → null
        |
        */

        resetOrgIntentionCandidateOnboardingAction:
          builder.mutation({

            query:
              onboardingReset => ({

                url:
                  '/orgIntentionCandidateOnboarding/reset',

                method:
                  'PATCH',

                body: {
                  ...onboardingReset,
                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Invalidate Candidate + Board Status
            |--------------------------------------------------------------------------
            */

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const onboardingId =
                  result
                    ?.onboarding
                    ?._id


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `BOARD-${arg?.intentionBoardId}`,
                  },


                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `CANDIDATE-${arg?.intentionCandidateId}`,
                  },


                  ...(
                    onboardingId
                      ?
                      [
                        {
                          type:
                            'OrgIntentionCandidateOnboarding',

                          id:
                            onboardingId,
                        },
                      ]
                      :
                      []
                  ),

                ]

              },

          }),



        /*
        |--------------------------------------------------------------------------
        | Archive Candidate Onboarding
        |--------------------------------------------------------------------------
        |
        | PATCH
        |
        | /orgIntentionCandidateOnboarding/archive
        |
        | Body:
        |
        | {
        |   orgId,
        |   intentionBoardId,
        |   intentionCandidateId
        | }
        |
        | Soft archive only.
        |
        */

        archiveOrgIntentionCandidateOnboarding:
          builder.mutation({

            query:
              onboardingArchive => ({

                url:
                  '/orgIntentionCandidateOnboarding/archive',

                method:
                  'PATCH',

                body: {
                  ...onboardingArchive,
                },


                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),


            /*
            |--------------------------------------------------------------------------
            | Invalidate Candidate + Board Status
            |--------------------------------------------------------------------------
            |
            | After archive:
            |
            | - single candidate GET should refetch and return derived state
            | - board status should refetch
            |
            */

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const onboardingId =
                  result
                    ?.onboarding
                    ?._id


                return [

                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `BOARD-${arg?.intentionBoardId}`,
                  },


                  {
                    type:
                      'OrgIntentionCandidateOnboarding',

                    id:
                      `CANDIDATE-${arg?.intentionCandidateId}`,
                  },


                  ...(
                    onboardingId
                      ?
                      [
                        {
                          type:
                            'OrgIntentionCandidateOnboarding',

                          id:
                            onboardingId,
                        },
                      ]
                      :
                      []
                  ),

                ]

              },

          }),


      }),

  })



/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

    /*
    |--------------------------------------------------------------------------
    | My Initiation - 27 Sep 2026
    |--------------------------------------------------------------------------
    */

    useGetMyOrgIntentionCandidateInitiationQuery,

    useLazyGetMyOrgIntentionCandidateInitiationQuery,

    /*
    |--------------------------------------------------------------------------
    | Board Onboarding Status
    |--------------------------------------------------------------------------
    */

    useGetOrgIntentionBoardCandidateOnboardingStatusQuery,

    useLazyGetOrgIntentionBoardCandidateOnboardingStatusQuery,


    /*
    |--------------------------------------------------------------------------
    | Board SlotBoard Status - 25 Sep 2026
    |--------------------------------------------------------------------------
    */

    useGetOrgIntentionBoardCandidateSlotStatusQuery,

    useLazyGetOrgIntentionBoardCandidateSlotStatusQuery,


    /*
    |--------------------------------------------------------------------------
    | Candidate Onboarding
    |--------------------------------------------------------------------------
    */

    useGetOrgIntentionCandidateOnboardingQuery,

    useLazyGetOrgIntentionCandidateOnboardingQuery,


    /*
    |--------------------------------------------------------------------------
    | Mutations
    |--------------------------------------------------------------------------
    */

    useUpdateOrgIntentionCandidateOnboardingMutation,

    useResetOrgIntentionCandidateOnboardingActionMutation,

    useArchiveOrgIntentionCandidateOnboardingMutation,

} =
  orgIntentionCandidateOnboardingsApiSlice



/*
|--------------------------------------------------------------------------
| Board Onboarding Status Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionBoardCandidateOnboardingStatusResult =
  ({
    orgId,
    intentionBoardId,
  }) =>
    orgIntentionCandidateOnboardingsApiSlice
      .endpoints
      .getOrgIntentionBoardCandidateOnboardingStatus
      .select({

        orgId,

        intentionBoardId,

      })



/*
|--------------------------------------------------------------------------
| Board Onboarding Status Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionBoardCandidateOnboardingStatusData =
  ({
    orgId,
    intentionBoardId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateOnboardingStatusResult({

        orgId,

        intentionBoardId,

      }),

      onboardingStatusResult =>
        onboardingStatusResult
          ?.data

    )



/*
|--------------------------------------------------------------------------
| Board Candidate Onboarding Status Selector
|--------------------------------------------------------------------------
|
| Select one candidate from the board onboarding response without
| initiating another API request.
|
*/

export const selectOrgIntentionBoardCandidateOnboardingStatusByCandidateId =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateOnboardingStatusData({

        orgId,

        intentionBoardId,

      }),

      onboardingStatusData => {

        return (
          onboardingStatusData
            ?.candidates
            ?.find(
              candidate =>
                String(
                  candidate
                    ?.intentionCandidateId
                ) ===
                String(
                  intentionCandidateId
                )
            )
          ||
          null
        )

      }

    )



/*
|--------------------------------------------------------------------------
| Board SlotBoard Status Query Result Selector - 25 Sep 2026
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionBoardCandidateSlotStatusResult =
  ({
    orgId,
    intentionBoardId,
  }) =>
    orgIntentionCandidateOnboardingsApiSlice
      .endpoints
      .getOrgIntentionBoardCandidateSlotStatus
      .select({

        orgId,

        intentionBoardId,

      })



/*
|--------------------------------------------------------------------------
| Board SlotBoard Status Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionBoardCandidateSlotStatusData =
  ({
    orgId,
    intentionBoardId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateSlotStatusResult({

        orgId,

        intentionBoardId,

      }),

      slotStatusResult =>
        slotStatusResult
          ?.data

    )



/*
|--------------------------------------------------------------------------
| Board Candidate SlotBoard Status Selector
|--------------------------------------------------------------------------
|
| Returns both:
|
| initial1on1
| deepDive
|
| for one candidate from the already cached board response.
|
*/

export const selectOrgIntentionBoardCandidateSlotStatusByCandidateId =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateSlotStatusData({

        orgId,

        intentionBoardId,

      }),

      slotStatusData => {

        return (
          slotStatusData
            ?.candidates
            ?.find(
              candidate =>
                String(
                  candidate
                    ?.intentionCandidateId
                ) ===
                String(
                  intentionCandidateId
                )
            )
          ||
          null
        )

      }

    )



/*
|--------------------------------------------------------------------------
| Candidate Initial 1:1 Slot Status Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateInitial1on1SlotStatus =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateSlotStatusByCandidateId({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      }),

      candidateSlotStatus =>
        candidateSlotStatus
          ?.initial1on1
        ||
        null

    )



/*
|--------------------------------------------------------------------------
| Candidate Deep Dive Slot Status Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateDeepDiveSlotStatus =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionBoardCandidateSlotStatusByCandidateId({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      }),

      candidateSlotStatus =>
        candidateSlotStatus
          ?.deepDive
        ||
        null

    )



/*
|--------------------------------------------------------------------------
| Candidate Onboarding Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateOnboardingResult =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    orgIntentionCandidateOnboardingsApiSlice
      .endpoints
      .getOrgIntentionCandidateOnboarding
      .select({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      })



/*
|--------------------------------------------------------------------------
| Candidate Onboarding Response Selector
|--------------------------------------------------------------------------
|
| Returns:
|
| {
|   exists,
|   onboarding,
|   summaryStatus
| }
|
*/

export const selectOrgIntentionCandidateOnboardingResponse =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionCandidateOnboardingResult({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      }),

      onboardingResult =>
        onboardingResult
          ?.data

    )



/*
|--------------------------------------------------------------------------
| Candidate Onboarding Data Selector
|--------------------------------------------------------------------------
|
| Convenience selector returning only the onboarding object.
|
*/

export const selectOrgIntentionCandidateOnboardingData =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionCandidateOnboardingResponse({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      }),

      onboardingResponse =>
        onboardingResponse
          ?.onboarding
        ||
        null

    )



/*
|--------------------------------------------------------------------------
| Candidate Onboarding Summary Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateOnboardingSummary =
  ({
    orgId,
    intentionBoardId,
    intentionCandidateId,
  }) =>
    createSelector(

      selectOrgIntentionCandidateOnboardingResponse({

        orgId,

        intentionBoardId,

        intentionCandidateId,

      }),

      onboardingResponse =>
        onboardingResponse
          ?.summaryStatus
        ||
        'not_started'

    )